import React from 'react';
import { Link } from 'react-router-dom';
import {
  Sparkles, TrendingUp, Sunrise, Globe2, Trophy, Gavel, BarChart3,
  ClipboardCheck, HelpCircle, LogOut, MessageCircle, Settings2, Bell, Camera,
} from 'lucide-react';
import { useAuth } from '../lib/auth';

/**
 * Mobile-first "More" page — tile grid focused on INFORMATION and ON-SITE tools.
 *
 * Philosophy:
 *  - Read-first: charts, alerts, listings, leaderboards, briefs
 *  - On-site: snags/inspection (uses camera), tasks, notifications
 *  - Deferred to desktop: BOQ, permissions, vendor CRUD, accounting details,
 *    property CRUD, team management, admin settings. These exist but live behind
 *    an "Admin (desktop)" collapsed footer link — not primary mobile UX.
 */

// Primary tiles — big, colorful, tap-friendly
const MARKET_TILES = [
  { to: '/app/daily-digest',       label: "Today's Brief",     icon: Sunrise,   grad: 'from-amber-400 to-orange-500',   tid: 'morning-brief' },
  { to: '/app/market/leaderboard', label: 'Yield Leaderboard', icon: Trophy,    grad: 'from-emerald-500 to-teal-600',  tid: 'rental-leaderboard' },
  { to: '/app/market/live',        label: 'Live Listings',     icon: Globe2,    grad: 'from-sky-500 to-indigo-600',    tid: 'live-listings' },
  { to: '/m/trends',               label: 'Market Trends',     icon: BarChart3, grad: 'from-rose-500 to-pink-600',     tid: 'trends' },
  { to: '/m/auctions',             label: 'Auctions',          icon: Gavel,     grad: 'from-purple-500 to-fuchsia-600',tid: 'auctions' },
  { to: '/m/deal-analyzer',        label: 'Deal Analyzer',     icon: Sparkles,  grad: 'from-[#679286] to-[#4a6d63]',   tid: 'deal-analyzer' },
];

// On-site / operations tiles — subset that makes sense on a phone
const ONSITE_TILES = [
  { to: '/m/tasks',           label: 'My Tasks',        icon: ClipboardCheck, grad: 'from-blue-500 to-cyan-600', tid: 'tasks' },
  { to: '/app/mobile-snag',   label: 'Snag & Inspect',  icon: Camera,         grad: 'from-orange-500 to-red-500', tid: 'snags' },
  { to: '/app/notifications', label: 'Alerts',          icon: Bell,           grad: 'from-slate-600 to-slate-800', tid: 'notifications' },
  { to: '/app/help',          label: 'Ask Iris / Help', icon: MessageCircle,  grad: 'from-[#93704A] to-[#7a5c3d]', tid: 'help' },
];

function Tile({ to, label, icon: Icon, grad, tid }) {
  return (
    <Link
      to={to}
      data-testid={`mobile-more-${tid}`}
      className={`relative aspect-square rounded-3xl overflow-hidden bg-gradient-to-br ${grad} text-white p-4 flex flex-col justify-between active:scale-95 transition-transform shadow-md`}
    >
      <div className="absolute -top-3 -right-3 opacity-20 pointer-events-none">
        <Icon size={80} strokeWidth={1.5}/>
      </div>
      <div className="w-11 h-11 rounded-2xl bg-white/20 backdrop-blur-sm grid place-items-center">
        <Icon size={22}/>
      </div>
      <div>
        <div className="font-medium text-[15px] leading-tight">{label}</div>
      </div>
    </Link>
  );
}

export default function MobileMore() {
  const { user, logout } = useAuth();
  const isAdmin = user && ['owner', 'md'].includes(user.role);
  return (
    <div className="min-h-screen pb-40" data-testid="mobile-more-page">
      {/* Simple header — greeting only, no back-x */}
      <header className="px-5 pt-8 pb-4">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Hi,</div>
        <h1 className="font-serif text-3xl text-slate-900 leading-tight">
          {user?.name?.split(' ')[0] || 'Investor'}
        </h1>
        <p className="text-xs text-slate-500 mt-1">
          What's the market telling you today?
        </p>
      </header>

      <div className="px-4 space-y-6">
        {/* User compact card */}
        <div className="flex items-center gap-3 p-3 rounded-2xl bg-white/80 backdrop-blur-sm border border-white/70" data-testid="mobile-more-user">
          <img
            src={user?.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || 'U')}&background=679286&color=fff`}
            alt="" className="w-11 h-11 rounded-full border-2 border-white shadow flex-shrink-0"
          />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate">{user?.name}</div>
            <div className="text-[10px] uppercase tracking-wider text-[#679286] font-semibold">{user?.role}</div>
          </div>
          <Link to="/app/profile" className="p-2 rounded-full hover:bg-slate-100" data-testid="mobile-more-profile">
            <Settings2 size={16} className="text-slate-500"/>
          </Link>
        </div>

        {/* Market intelligence — big square tiles */}
        <section>
          <div className="flex items-baseline justify-between mb-3 px-1">
            <h2 className="text-[13px] font-semibold text-slate-900">Market Intelligence</h2>
            <span className="text-[10px] uppercase tracking-widest text-slate-400">Live</span>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {MARKET_TILES.map(t => <Tile key={t.to} {...t}/>)}
          </div>
        </section>

        {/* On-site / operations */}
        <section>
          <div className="flex items-baseline justify-between mb-3 px-1">
            <h2 className="text-[13px] font-semibold text-slate-900">On-site & Alerts</h2>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {ONSITE_TILES.map(t => <Tile key={t.to} {...t}/>)}
          </div>
        </section>

        {/* Admin — a single compact link at the bottom. Full admin experience is desktop. */}
        {isAdmin && (
          <section className="pt-2">
            <div className="text-[11px] text-slate-400 px-1 mb-2 uppercase tracking-widest">Admin (best on desktop)</div>
            <div className="grid grid-cols-2 gap-2">
              <Link
                to="/app/dashboard"
                data-testid="mobile-more-admin-dashboard"
                className="p-3 rounded-2xl bg-white/60 border border-white/70 text-slate-700 text-xs flex items-center gap-2"
              >
                <BarChart3 size={14} className="text-slate-500"/> Executive Dashboard
              </Link>
              <Link
                to="/app/admin"
                data-testid="mobile-more-admin-settings"
                className="p-3 rounded-2xl bg-white/60 border border-white/70 text-slate-700 text-xs flex items-center gap-2"
              >
                <Settings2 size={14} className="text-slate-500"/> Admin Settings
              </Link>
            </div>
            <div className="text-[10px] text-slate-400 mt-2 px-1 italic">
              Vendor CRUD, BOQ, permissions, accounting details &amp; team management are optimized for desktop.
            </div>
          </section>
        )}

        <button
          onClick={logout}
          data-testid="mobile-more-logout"
          className="w-full mt-6 py-3.5 rounded-2xl bg-white/60 border border-white/70 text-red-600 font-medium flex items-center justify-center gap-2 active:scale-95 transition-transform"
        >
          <LogOut size={16}/> Log out
        </button>

        <div className="text-center text-[10px] text-slate-400 mt-4">
          Iris Realty · Property OS for Dubai
        </div>
      </div>
    </div>
  );
}
